/**
 * Donor portfolio — visualises each sponsored child's photo, marksheets,
 * progress reports + SAC activity reports & achievements. Each downloadable
 * file opens via /api/files/* in a new tab.
 *
 * Also lists the donor's donation history with a "Download 80G receipt"
 * button per row (calls /api/donations/{id}/80g-receipt.pdf).
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DownloadSimple, Student, Image as ImageIcon, Books, ChartLineUp, Trophy,
  Receipt as ReceiptIcon,
} from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function DonorPortfolio() {
  const [kids, setKids] = useState([]);
  const [donations, setDonations] = useState([]);

  useEffect(() => {
    api.get("/donor/portfolio").then((r) => setKids(r.data || [])).catch(() => {});
    api.get("/donor/donations").then((r) => setDonations(r.data || [])).catch(() => {});
  }, []);

  return (
    <DashboardLayout title="My sponsored children" subtitle="Photos, marksheets, progress reports — and your 80G receipts.">
      <Tabs defaultValue="children">
        <TabsList>
          <TabsTrigger value="children" data-testid="donor-tab-children"><Student size={12} className="mr-1.5" /> Children</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="donor-tab-receipts"><ReceiptIcon size={12} className="mr-1.5" /> 80G receipts</TabsTrigger>
        </TabsList>

        <TabsContent value="children">
          {kids.length === 0 ? (
            <div className="vs-card p-10 text-center text-[var(--vs-text-secondary)]" data-testid="donor-portfolio-empty">
              No sponsored children yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {kids.map((k) => <ChildCard key={k.id} child={k} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="receipts">
          <DonationsTable donations={donations} />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function ChildCard({ child }) {
  const photoUrl = child.photo_path
    ? `${API_BASE}/api/files/photo?path=${encodeURIComponent(child.photo_path)}`
    : null;

  const marksheetUrl = (id) => `${API_BASE}/api/files/marksheet?marksheet_id=${id}`;
  const sacFileUrl = (path) => `${API_BASE}/api/files/sac?path=${encodeURIComponent(path)}`;

  return (
    <div className="vs-card p-5" data-testid={`donor-child-${child.id}`}>
      <div className="flex items-start gap-4 mb-4">
        {photoUrl ? (
          <img src={photoUrl} alt={child.name} className="w-20 h-20 rounded-lg object-cover border border-[var(--vs-border)]" data-testid={`donor-child-photo-${child.id}`} />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-[var(--vs-bg-subtle)] grid place-items-center text-[var(--vs-text-secondary)]" data-testid={`donor-child-photo-empty-${child.id}`}>
            <ImageIcon size={26} weight="duotone" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-semibold">{child.name}</div>
          <div className="text-sm text-[var(--vs-text-secondary)] font-mono">{child.vs_id} · Class {child.class_name || "—"} {child.section ? `(${child.section})` : ""}</div>
          {child.sac?.what_child_wants_to_be && (
            <div className="text-xs text-[var(--vs-secondary)] mt-1 flex items-center gap-1">
              <Trophy size={11} weight="duotone" /> Wants to be: <span className="font-medium">{child.sac.what_child_wants_to_be}</span>
            </div>
          )}
        </div>
      </div>

      {child.sac?.any_achievement && (
        <div className="vs-card p-3 mb-3 bg-[var(--vs-bg-subtle)]" data-testid={`donor-child-achievement-${child.id}`}>
          <div className="eyebrow mb-1">Achievement</div>
          <div className="text-sm">{child.sac.any_achievement}</div>
        </div>
      )}

      <Section label="Marksheets" icon={<Books size={11} />}>
        {child.marksheets.length === 0 ? (
          <Empty>No marksheets published yet.</Empty>
        ) : (
          <ul className="space-y-2">
            {child.marksheets.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`donor-marksheet-${m.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.exam_name} <span className="text-[var(--vs-text-secondary)] font-mono text-xs ml-1">{m.academic_year}</span></div>
                  <div className="text-xs text-[var(--vs-text-secondary)]">{m.total_obtained}/{m.total_max} · {fmtDate(m.published_at)}</div>
                </div>
                <Button asChild size="sm" variant="outline" data-testid={`donor-marksheet-dl-${m.id}`}>
                  <a href={marksheetUrl(m.id)} target="_blank" rel="noopener noreferrer"><DownloadSimple size={12} className="mr-1" /> PDF</a>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section label="Progress reports" icon={<ChartLineUp size={11} />}>
        {child.progress_reports.length === 0 ? (
          <Empty>No progress reports yet.</Empty>
        ) : (
          <ul className="space-y-2">
            {child.progress_reports.map((p) => (
              <li key={p.id} className="text-sm vs-card p-3" data-testid={`donor-progress-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="eyebrow">{p.period || "—"}</div>
                  <div className="text-xs font-mono text-[var(--vs-text-secondary)]">{fmtDate(p.created_at)}</div>
                </div>
                {p.highlights && <p className="text-sm">{p.highlights}</p>}
                {p.challenges && <p className="text-xs text-[var(--vs-text-secondary)] mt-1">⚠ {p.challenges}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {(child.sac?.activity_reports?.length || 0) > 0 && (
        <Section label="Activity reports (SAC)" icon={<Trophy size={11} />}>
          <ul className="space-y-2">
            {child.sac.activity_reports.map((path, idx) => (
              <li key={path} className="flex items-center justify-between gap-3 text-sm" data-testid={`donor-activity-${child.id}-${idx}`}>
                <div className="font-mono text-xs truncate text-[var(--vs-text-secondary)]">{path.split("/").pop()}</div>
                <Button asChild size="sm" variant="outline">
                  <a href={sacFileUrl(path)} target="_blank" rel="noopener noreferrer"><DownloadSimple size={12} /></a>
                </Button>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Section({ label, icon, children }) {
  return (
    <div className="mb-3">
      <div className="eyebrow mb-2 flex items-center gap-1">{icon}{label}</div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-xs text-[var(--vs-text-secondary)] italic">{children}</div>;
}

function DonationsTable({ donations }) {
  if (!donations.length) {
    return <div className="vs-card p-10 text-center text-[var(--vs-text-secondary)]" data-testid="donor-donations-empty">No donations yet.</div>;
  }
  return (
    <div className="vs-card overflow-x-auto" data-testid="donor-donations-table">
      <table className="w-full text-sm">
        <thead className="bg-[var(--vs-bg-subtle)]">
          <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Receipt no</th>
            <th className="px-4 py-3">Purpose</th>
            <th className="px-4 py-3">Method</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {donations.map((d) => (
            <tr key={d.id} className="border-t border-[var(--vs-border)]" data-testid={`donor-donation-row-${d.id}`}>
              <td className="px-4 py-3 font-mono text-xs">{fmtDate(d.created_at)}</td>
              <td className="px-4 py-3 font-mono text-xs">{d.receipt_no_80g || <span className="text-[var(--vs-text-secondary)]">pending</span>}</td>
              <td className="px-4 py-3 capitalize">{d.receipt_donation_for || d.category}</td>
              <td className="px-4 py-3"><Badge variant="outline">{d.method || "online"}</Badge></td>
              <td className="px-4 py-3 font-display font-semibold">{fmtINR(d.amount)}</td>
              <td className="px-4 py-3 text-right">
                {d.receipt_no_80g ? (
                  <Button asChild size="sm" variant="outline" data-testid={`donor-receipt-dl-${d.id}`}>
                    <a href={`${API_BASE}/api/donations/${d.id}/80g-receipt.pdf`} target="_blank" rel="noopener noreferrer">
                      <DownloadSimple size={12} className="mr-1" /> 80G PDF
                    </a>
                  </Button>
                ) : <span className="text-xs text-[var(--vs-text-secondary)]">receipt pending</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
